/**
 * Custom React Query hooks for AI executions
 * Provides data fetching, mutations, and real-time polling for agent executions
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import client from "@/app/api";
import { agentExecutionKeys } from "@/query/keys/agent-executions";
import {
  listAgentExecutionsV2,
  getAgentExecutionFilterOptions,
} from "@/lib/v2-api/agent-executions-client";
import { toast } from "sonner";
import type {
  AgentExecutionResponse,
  PaginatedAgentExecutionsResponse,
} from "@/app/api/types/agent-executions";
import type { UUID } from "crypto";

// Status constants for easy reference
export const EXECUTION_STATUS = {
  PENDING: 0,
  RUNNING: 1,
  COMPLETED: 2,
  FAILED: 3,
  CANCELLED: 4,
} as const;

export type ExecutionStatusValue =
  (typeof EXECUTION_STATUS)[keyof typeof EXECUTION_STATUS];

// Helper functions for status checks
export function isExecutionActive(status: number): boolean {
  return (
    status === EXECUTION_STATUS.PENDING || status === EXECUTION_STATUS.RUNNING
  );
}

export function isExecutionTerminal(status: number): boolean {
  return (
    status === EXECUTION_STATUS.COMPLETED ||
    status === EXECUTION_STATUS.FAILED ||
    status === EXECUTION_STATUS.CANCELLED
  );
}

interface UseAIExecutionsOptions {
  status?: number;
  agentType?: string;
  search?: string;
  channel?: string;
  /** UTC ISO start bound — converted from the company-tz picker at the boundary. */
  from?: string;
  /** UTC ISO end bound. */
  to?: string;
  limit?: number;
  offset?: number;
  pollingInterval?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch and poll agent executions with pagination
 * Automatically polls when there are active (running/pending) executions.
 *
 * Served by the V2 (TypeScript) list endpoint via `listAgentExecutionsV2`;
 * detail + cancel/delete/retry still hit the legacy Python client.
 */
export function useAIExecutions(options: UseAIExecutionsOptions = {}) {
  const {
    status,
    agentType,
    search,
    channel,
    from,
    to,
    limit = 50,
    offset = 0,
    pollingInterval = 5000,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: agentExecutionKeys.list({
      status,
      agent_type: agentType,
      search,
      channel,
      from,
      to,
      limit,
      offset,
    }),
    queryFn: () =>
      listAgentExecutionsV2({
        status,
        agentType,
        search,
        channel,
        from,
        to,
        limit,
        offset,
      }),
    refetchInterval: (query) => {
      const data = query.state.data as
        | PaginatedAgentExecutionsResponse
        | undefined;
      // Poll fast when active executions exist, slow otherwise to detect new ones
      const hasActive = data?.items?.some(
        (e) => e.status === 0 || e.status === 1,
      );
      return hasActive ? pollingInterval : 30000;
    },
    enabled,
    // Keep previous data during refetch to avoid flickering
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30, // 30s stale time — list is lightweight now
  });
}

/**
 * Hook to fetch the distinct `agent_type` values for the authed company,
 * used to populate the agent-type filter dropdown. Long stale time — the set
 * of agent types changes rarely.
 */
export function useAgentExecutionFilterOptions() {
  return useQuery({
    queryKey: agentExecutionKeys.filterOptions(),
    queryFn: getAgentExecutionFilterOptions,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch a single agent execution with real-time updates
 * Polls more frequently for active executions
 */
export function useAIExecutionDetail(executionId: string | null) {
  return useQuery({
    queryKey: agentExecutionKeys.detail(executionId!),
    queryFn: () =>
      client.agentExecutions.getAgentExecutionById(executionId! as UUID),
    enabled: !!executionId,
    refetchInterval: (query) => {
      const data = query.state.data as AgentExecutionResponse | undefined;
      // Poll more frequently for active executions
      return data && (data.status === 0 || data.status === 1) ? 2000 : false;
    },
  });
}

/**
 * Hook to cancel a running or pending execution
 */
export function useCancelExecution() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (executionId: string) =>
      client.agentExecutions.cancelAgentExecution(executionId as UUID),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentExecutionKeys.all,
      });
      toast.success(t("ai.execution.cancelled"));
    },
    onError: (error: Error) => {
      toast.error(`${t("ai.execution.failedToCancel")}: ${error.message}`);
    },
  });
}

/**
 * Hook to retry a failed or cancelled execution
 * Creates a new execution and deletes the old one
 */
export function useRetryExecution() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (executionId: string) =>
      client.agentExecutions.retryAgentExecution(executionId as UUID),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentExecutionKeys.all,
      });
      toast.success(t("ai.execution.retried"));
    },
    onError: (error: Error) => {
      if (error.message.includes("Maximum retries")) {
        toast.error(t("ai.execution.maxRetryReached"));
      } else {
        toast.error(`${t("ai.execution.failedToRetry")}: ${error.message}`);
      }
    },
  });
}

/**
 * Hook to delete a completed, failed, or cancelled execution
 */
export function useDeleteExecution() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (executionId: string) =>
      client.agentExecutions.deleteAgentExecution(executionId as UUID),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agentExecutionKeys.all,
      });
      toast.success(t("ai.execution.deleted"));
    },
    onError: (error: Error) => {
      toast.error(`${t("ai.execution.failedToDelete")}: ${error.message}`);
    },
  });
}
